import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import {
  useGetOnboardingModulesAPI,
  useGetOnboardingProgressAPI,
  useUpdateOnboardingProgressAPI
} from "@/api/onboarding/useOnboardingAPI";
import {
  selectOnboardingModuleCode,
  setActiveStepTourId
} from "@/store/slice/onboardingSlice";
import type {
  OnboardingProgress,
  OnboardingProgressStatus
} from "@/types/onboarding";

import { getModuleConfig } from "../configs/getModuleConfig";
import type { ModuleConfig } from "../configs/types";

/**
 * Gives the zero-based step index where a module starts again.
 * For an in-progress module, this is the last active step.
 * For a not-started or a completed module, this is 0.
 *
 * useEnterOnboardingMode uses this function to find the seededData entry of a
 * resumed module.
 */
export function resolveStartIndex(
  config: ModuleConfig | null,
  progress: OnboardingProgress | null
): number {
  if (!config || !progress) return 0;
  if (progress.status === "in_progress" && progress.current_step != null) {
    return Math.max(
      0,
      Math.min(progress.current_step - 1, config.steps.length - 1)
    );
  }
  return 0; // not_started and completed start at step 1
}

/**
 * Keeps the state of the tour steps.
 * The hook holds the current step, moves between steps, and writes the
 * onboarding progress.
 */
export function useTourStep() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const moduleCode = useSelector(selectOnboardingModuleCode);

  const { data: modules } = useGetOnboardingModulesAPI({
    enabled: Boolean(moduleCode)
  });

  const { data: progressResponse } = useGetOnboardingProgressAPI(undefined, {
    enabled: Boolean(moduleCode)
  });

  const updateProgress = useUpdateOnboardingProgressAPI();

  const moduleId =
    modules?.modules.find((m) => m.code === moduleCode)?.id ?? null;
  const progress =
    progressResponse?.progress.find((p) => p.module_id === moduleId) ?? null;

  const config = getModuleConfig(moduleCode);
  const startIndex = resolveStartIndex(config, progress);

  // The state stays null until the user moves to a different step.
  // redux-persist gives moduleCode before the progress query is complete.
  // A too-early 0 runs the onActivate of step 0 before the resume step.
  const [stepIndexState, setStepIndexState] = useState<number | null>(null);

  // Derive the active index. Do not set state during render.
  // Use startIndex while the state is null, then use the state.
  // The first write from goToNext or goToPrev is permanent.
  const stepIndex =
    stepIndexState === null && config && modules && progressResponse
      ? startIndex
      : stepIndexState;

  const currentStep =
    stepIndex === null ? null : (config?.steps[stepIndex] ?? null);
  const nextStep =
    stepIndex === null ? null : (config?.steps[stepIndex + 1] ?? null);
  const currentStepRoute = currentStep
    ? (currentStep.route ?? config?.route ?? "/")
    : null;
  const totalSteps = config?.steps.length ?? 0;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;

  // A step can have a different route than its module.
  // Go to that route when the step becomes active.
  // useTargetElement waits for the target on the new route.
  useEffect(() => {
    if (!currentStepRoute || location.pathname === currentStepRoute) return;

    navigate(currentStepRoute);
  }, [currentStepRoute, location.pathname, navigate]);

  // Run onActivate for each new step, also after a page refresh.
  // After a refresh, the tour starts at the saved step with no user action.
  // Steps with a target in a popup must open that popup again.
  useEffect(() => {
    const teardown = currentStep?.onActivate?.();

    // teardown is undefined for a step that only opens a popup.
    // If teardown is present, it stops an async side effect of the step.
    // It does not close the popup. See TourStep.onActivate in configs/types.ts.
    return () => {
      teardown?.();
    };
    // Run again only when the step id changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep?.id]);

  // Show the tourId of the active step to components out of the tour.
  useEffect(() => {
    dispatch(setActiveStepTourId(currentStep?.tourId ?? null));

    return () => {
      dispatch(setActiveStepTourId(null));
    };
  }, [dispatch, currentStep?.tourId]);

  // No useCallback on the handlers below. OnboardingTour makes new closures
  // for them at each render, and the updateProgress mutation object gets a
  // new reference at each render.

  const writeProgress = async (
    status: OnboardingProgressStatus,
    stepOneBased: number
  ) => {
    if (!config || !moduleId) return;

    await updateProgress.mutateAsync({
      moduleId,
      body: {
        current_step: stepOneBased,
        status,
        walkthrough_version: config.version
      }
    });
  };

  const goToNext = async () => {
    if (!config || stepIndex === null) return;

    currentStep?.onNext?.();

    const nextStepIndex = isLast ? stepIndex : stepIndex + 1;

    if (!isLast) setStepIndexState(nextStepIndex);

    await writeProgress(
      isLast ? "completed" : "in_progress",
      nextStepIndex + 1
    );
  };

  const goToPrev = () => {
    // Use stepIndex, not stepIndexState. After a resume, the user can click
    // Prev first. Then stepIndexState is null, but stepIndex has startIndex.
    // isFirst makes sure that stepIndex is a number more than 0.
    if (isFirst || stepIndex === null) return;

    currentStep?.onPrev?.();

    setStepIndexState(stepIndex - 1);
  };

  const skip = async () => {
    if (stepIndex === null) return;

    await writeProgress("in_progress", stepIndex + 1);
  };

  return {
    currentStep,
    nextStep,
    stepIndex,
    totalSteps,
    isFirst,
    isLast,
    goToNext,
    goToPrev,
    skip
  };
}
